<template>
  <button
    class="rounded-10 md:rounded-6 drop-shadow-2 hover:drop-shadow-3 w-full lg:max-w-sm flex-1 md:bg-white min-h-20 md:min-w-80 md:min-h-24"
    @click="mmrModalOpen = true"
  >
    <MSpinner
      v-show="!hasChartData || !midMarketRate"
      size="h-12 w-12"
      class="m-auto"
      color="text-gray-300"
      stroke-width="3"
    />
    <Transition
      enter-active-class="transition-opacity ease-out duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      appear
    >
      <div v-show="hasChartData && midMarketRate" class="flex justify-between items-center w-full">
        <div class="py-1 text-left text-gray-900">
          <span v-if="!noDescription" class="block text-12 lg:text-14 mb-1">
            {{ $t('currency_chart.description') }}
          </span>
          <span
            v-if="midMarketRate"
            class="lg:text-18"
            :class="{ 'font-semibold': !IS_MOBILE_APP }"
          >
            1 {{ currencyFromIso }} = {{ formatRate(midMarketRate, $lang) }}
            {{ currencyToIso }}
          </span>
          <ChartTrendMessage
            v-if="processedData && midMarketRate"
            :chart-data="processedData"
            :mid-market-rate="midMarketRate"
            class="text-12"
          />
        </div>
        <div class="flex items-center">
          <CurrencyChart
            :currency-from="currencyFromIso"
            :currency-to="currencyToIso"
            :mid-market-rate="midMarketRate"
            size="thumbnail"
            chart-class="h-12 w-20 md:h-20 md:w-20"
            no-fill
            :chart-option-overrides="chartOptionOverrides"
            @chart-data="processChartData"
          />
          <svg role="presentation" focusable="false" tabindex="-1" class="flex-none self-end ml-1 mb-1"
             
            alt="expand"
            svg-inline
           xmlns="http://www.w3.org/2000/svg" width="17" height="17" fill="none" xmlns:v="https://vecta.io/nano"><title>expand</title><rect x=".466" y="1.206" width="15.25" height="15.25" rx="2.625" stroke="#737373" stroke-width=".75"/><path d="M12.079 7.819a.3.3 0 0 0 .512-.212V4.631a.3.3 0 0 0-.3-.3H9.315a.3.3 0 0 0-.212.512l.921.921a.3.3 0 0 1 0 .424l-4.576 4.576a.3.3 0 0 1-.424 0l-.921-.921a.3.3 0 0 0-.512.212v2.976a.3.3 0 0 0 .3.3h2.976a.3.3 0 0 0 .212-.512l-.921-.921a.3.3 0 0 1 0-.424l4.576-4.576a.3.3 0 0 1 .424 0l.921.921z" fill="#737373"/></svg>
        </div>
      </div>
    </Transition>
    <MidMarketRateModal
      v-if="mmrModalOpen && midMarketRate"
      :mid-market-rate="midMarketRate"
      :currency-from-iso="currencyFromIso"
      :currency-to-iso="currencyToIso"
      @close="mmrModalOpen = false"
      @open-subscribe-modal="openSignUpModal = true"
    />
    <SubscribeModal
      v-if="openSignUpModal && comparisonId"
      :comparison-id="comparisonId"
      :current-tab="currentTab"
      :signed-in="$cookie.getSigned()"
      :title="$t('comparison_alert.form_title')"
      creation-location="mmr_modal"
      @close="openSignUpModal = false"
    />
  </button>
</template>

<script lang="ts" setup>
import type { ChartOptions, ChartData } from 'chart.js'
import type { PaymentType } from '@core/types/imt'
import MidMarketRateModal from './mid-market-rate-modal.vue'
import SubscribeModal from '../subscribe-modal.vue'

const IS_MOBILE_APP = __IS_MOBILE_APP__

defineProps<{
  midMarketRate?: number
  comparisonId?: string
  currentTab: PaymentType
  currencyFromIso: string
  currencyToIso: string
  noDescription?: boolean
}>()

const { $t, $lang, $cookie } = useNuxtApp()

const mmrModalOpen = ref(false)
const openSignUpModal = ref(false)
const hasChartData = ref(false)

const processedData = ref<Record<string, ChartData> | null>(null)

const chartOptionOverrides = computed<ChartOptions<'line'>>(() => {
  const opts: ChartOptions<'line'> = {
    maintainAspectRatio: false,
    scales: {
      x: { display: false },
      y: { display: false },
    },
    events: [],
  }
  if (opts.elements?.line) {
    opts.elements.line.borderWidth = 2
  }
  return opts
})

function processChartData(chartData: Record<string, ChartData> | null) {
  hasChartData.value = Boolean(chartData)
  if (chartData && changedIn2months(chartData)) {
    processedData.value = chartData
  }
}
</script>
